import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconChevronDown, IconLock, IconPlayerPlay, IconPlus, IconUserOff, IconUsers } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';
import { useOrg } from '@/hooks/use-org';
import { useOrgWidgets } from '@/hooks/use-org-widgets';
import { OrganizationMembersSubscription } from './-components/graphql';

const ROLE_COLORS: Record<string, string> = {
  owner: 'indigo',
  admin: 'cyan',
  member: 'gray',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'var(--mantine-color-green-6)',
  invited: 'var(--mantine-color-yellow-5)',
  disabled: 'var(--mantine-color-red-6)',
};

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'member', label: 'Member' },
];

type MemberRole = 'owner' | 'admin' | 'member';

export const Route = createFileRoute('/_authed/settings/members')({
  component: SettingsMembersPage,
});

function InviteModal({
  organizationId,
  opened,
  onClose,
}: {
  organizationId: string;
  opened: boolean;
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string | null>('member');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invite = async () => {
    if (!role) return;

    setIsSending(true);
    setError(null);

    const { error: apiError } = await api
      .organizations({ organizationId })
      .members.post({ email: email.trim(), role: role as MemberRole });

    setIsSending(false);

    if (apiError) return setError(getErrorMessage(apiError, 'Failed to send the invitation'));

    setEmail('');
    setRole('member');
    onClose();
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Invite Member">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void invite();
        }}
      >
        <Stack gap="md">
          <ErrorAlert message={error} />
          <TextInput
            label="Email"
            placeholder="user@example.com"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
            data-autofocus
          />
          <Select label="Role" data={ROLE_OPTIONS} value={role} onChange={setRole} />
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSending} disabled={!email.trim() || !role}>
              Send Invite
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function OwnerRoleBadge() {
  return (
    <Group gap={6}>
      <Badge variant="light" color={ROLE_COLORS.owner}>
        owner
      </Badge>
      <Tooltip label="Owners can only be changed by a platform operator">
        <IconLock size={12} color="var(--mantine-color-dimmed)" />
      </Tooltip>
    </Group>
  );
}

function EditableRoleBadge({ role, onChange }: { role: string; onChange: (role: MemberRole) => void }) {
  return (
    <Menu>
      <Menu.Target>
        <Badge
          variant="light"
          color={ROLE_COLORS[role] ?? 'gray'}
          style={{ cursor: 'pointer' }}
          rightSection={<IconChevronDown size={10} />}
        >
          {role}
        </Badge>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={() => onChange('admin')}>Admin</Menu.Item>
        <Menu.Item onClick={() => onChange('member')}>Member</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

function Members({ organizationId }: { organizationId: string }) {
  const { hasContactCenter } = useOrgWidgets();
  const [{ data }] = useSubscription({ query: OrganizationMembersSubscription, variables: { organizationId } });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!data) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const changeRole = async (memberId: string, role: MemberRole) => {
    setError(null);

    const { error: apiError } = await api.organizations({ organizationId }).members({ memberId }).patch({ role });

    if (apiError) setError(getErrorMessage(apiError, 'Failed to change the member role'));
  };

  const disable = async (memberId: string) => {
    setError(null);

    const { error: apiError } = await api.organizations({ organizationId }).members({ memberId }).disable.post();

    if (apiError) setError(getErrorMessage(apiError, 'Failed to disable the member'));
  };

  const enable = async (memberId: string) => {
    setError(null);

    const { error: apiError } = await api.organizations({ organizationId }).members({ memberId }).enable.post();

    if (apiError) setError(getErrorMessage(apiError, 'Failed to enable the member'));
  };

  return (
    <>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-end">
          <div>
            <Title order={2}>Members</Title>
            <Text size="sm" c="dimmed" mt="2xs">
              {data.organizationMembers.length} member{data.organizationMembers.length !== 1 ? 's' : ''}
            </Text>
          </div>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setInviteOpen(true)}>
            Invite Member
          </Button>
        </Group>

        <ErrorAlert message={error} />

        {data.organizationMembers.length === 0 ? (
          <Paper p="3xl" radius="lg" withBorder>
            <Stack align="center" gap="md">
              <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                <IconUsers size={24} />
              </ThemeIcon>
              <div style={{ textAlign: 'center' }}>
                <Text fw={500}>No members yet</Text>
                <Text size="sm" c="dimmed" mt="2xs">
                  Invite people to collaborate in this organization.
                </Text>
              </div>
              <Button variant="light" leftSection={<IconPlus size={16} />} onClick={() => setInviteOpen(true)}>
                Invite Member
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Paper p={0} radius="lg" style={{ overflow: 'hidden' }} withBorder>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Joined</Table.Th>
                  {hasContactCenter && <Table.Th>Extension</Table.Th>}
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.organizationMembers.map((member) => (
                  <Table.Tr key={member.id} opacity={member.status === 'disabled' ? 0.6 : 1}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {member.email}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {member.role === 'owner' ? (
                        <OwnerRoleBadge />
                      ) : (
                        <EditableRoleBadge role={member.role} onChange={(role) => void changeRole(member.id, role)} />
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: STATUS_COLORS[member.status] ?? 'var(--mantine-color-gray-5)',
                          }}
                        />
                        <Text size="sm" c="dimmed" tt="capitalize">
                          {member.status}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {member.joinedAt ? new Date(member.joinedAt).toLocaleDateString() : '—'}
                      </Text>
                    </Table.Td>
                    {hasContactCenter && (
                      <Table.Td>
                        <Text size="sm" c="dimmed" ff="monospace">
                          {member.extensionNumber ?? '—'}
                        </Text>
                      </Table.Td>
                    )}
                    <Table.Td>
                      {member.role !== 'owner' &&
                        (member.status === 'disabled' ? (
                          <Tooltip label="Enable member">
                            <ActionIcon variant="subtle" color="green" onClick={() => void enable(member.id)}>
                              <IconPlayerPlay size={14} />
                            </ActionIcon>
                          </Tooltip>
                        ) : (
                          <Tooltip label="Disable member">
                            <ActionIcon variant="subtle" color="red" onClick={() => void disable(member.id)}>
                              <IconUserOff size={14} />
                            </ActionIcon>
                          </Tooltip>
                        ))}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>

      <InviteModal organizationId={organizationId} opened={inviteOpen} onClose={() => setInviteOpen(false)} />
    </>
  );
}

function SettingsMembersPage() {
  const { organizationId } = useOrg();

  if (!organizationId) {
    return (
      <Center h="30vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return <Members organizationId={organizationId} />;
}
